/**
 * /vaccines/uk and /vaccines/us — country schedule overview.
 *
 * Renders the country's schedule grouped by age cohort, with each cell
 * showing dose number, year and a save chip per row. Below the table is
 * a per-vaccine list with quick navigation into single-vaccine pages.
 *
 * Q1: What is it?       → eyebrow + intro
 * Q2: Connected to?     → cohort links + vaccine links
 * Q3: Evidence?         → per-row source link
 * Q4: Where next?       → compare-with-other-country CTA
 */
import React, { useEffect, useState, useMemo } from "react";
import { useParams, useLocation, Link, Navigate } from "react-router-dom";
import { Calendar, Syringe, ExternalLink } from "lucide-react";
import api from "../../lib/api";
import SEO from "../../components/SEO";
import SaveButton from "../../components/research/SaveButton";
import CiteAndPrint from "../../components/research/CiteAndPrint";
import { countryByCode, AGE_COHORTS } from "../../lib/vaccineCohorts";

export default function CountrySchedulePage() {
    const { country: countryParam } = useParams();
    const location = useLocation();
    // Country resolution: explicit param wins; otherwise derive from path
    // (so /vaccines/uk and /vaccines/us work without a useParams hit).
    const derived = countryParam
        || (location.pathname.endsWith("/uk") ? "UK"
        :  location.pathname.endsWith("/us") ? "US"
        : null);
    const country = countryByCode(derived);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!country) { setLoading(false); return; }
        api.get(`/schedules/by-country/${country.code}`)
            .then(({ data }) => setData(data))
            .catch(() => setData({ rows: [], vaccine_count: 0 }))
            .finally(() => setLoading(false));
    }, [country]);

    const grouped = useMemo(() => {
        if (!data?.rows) return {};
        const out = {};
        for (const r of data.rows) {
            const key = `${r.age_months}::${r.age_label}`;
            (out[key] = out[key] || { age_months: r.age_months, age_label: r.age_label, rows: [] }).rows.push(r);
        }
        return out;
    }, [data]);

    if (!country) return <Navigate to="/vaccines" replace />;
    if (loading) return <div className="min-h-screen bg-[#0B1220] text-[#94A3B8] flex items-center justify-center">Loading…</div>;

    const orderedAges = Object.values(grouped).sort((a, b) => a.age_months - b.age_months);

    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "Dataset",
        name: `${country.label} routine childhood vaccination schedule`,
        description: `Official ${country.regulator} childhood vaccination schedule with linked TCP research.`,
        url: `https://library.thecontextproject.com/vaccines/${country.code.toLowerCase()}`,
    };

    const otherCountryCode = country.code === "UK" ? "us" : "uk";

    return (
        <div className="min-h-screen bg-[#0B1220] text-[#CBD5E1]" data-testid={`country-schedule-${country.code.toLowerCase()}`}>
            <SEO
                title={`${country.label} childhood vaccination schedule`}
                description={`Official ${country.regulator} schedule, explored with linked research, regulatory documents and TCP investigations.`}
                canonicalPath={`/vaccines/${country.code.toLowerCase()}`}
                jsonLd={jsonLd}
            />
            <div className="h-px bg-gradient-to-r from-transparent via-[#56CCF2]/70 to-transparent" />

            {/* Breadcrumbs */}
            <nav className="max-w-5xl mx-auto px-6 pt-6 text-[11px] text-[#64748B]" aria-label="Breadcrumb">
                <Link to="/" className="hover:text-[#56CCF2]">Library</Link>
                <span className="mx-1.5">›</span>
                <Link to="/vaccines" className="hover:text-[#56CCF2]">Vaccines</Link>
                <span className="mx-1.5">›</span>
                <span className="text-[#94A3B8]">{country.label}</span>
            </nav>

            {/* Hero */}
            <header className="max-w-5xl mx-auto px-6 pt-8 pb-12">
                <div className="flex items-start justify-between gap-6">
                    <div className="min-w-0 flex-1">
                        <div className="text-[10px] uppercase tracking-[0.28em] text-[#56CCF2] font-semibold mb-2.5">{country.regulator} · Routine schedule</div>
                        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight leading-[1.05] mb-4">
                            {country.label} childhood vaccination schedule
                        </h1>
                        <p className="text-[15px] text-[#94A3B8] leading-relaxed max-w-3xl">
                            The official {country.regulator} schedule, presented alongside structured evidence.
                            Each vaccine links to its full research record — including ingredients, manufacturer, regulatory documents and TCP investigations.
                        </p>
                    </div>
                    <div className="shrink-0">
                        <SaveButton
                            kind="vaccine_schedule"
                            refSlug={`country-${country.code.toLowerCase()}`}
                            refTitle={`${country.label} schedule`}
                            refUrl={`/vaccines/${country.code.toLowerCase()}`}
                            variant="pill"
                        />
                    </div>
                </div>
                <div className="mt-6">
                    <CiteAndPrint
                        title={`${country.label} childhood vaccination schedule`}
                        canonicalPath={`/vaccines/${country.code.toLowerCase()}`}
                        slug={`country-${country.code.toLowerCase()}`}
                    />
                </div>
            </header>

            {/* Schedule by age */}
            <section className="max-w-5xl mx-auto px-6 pb-12 space-y-6">
                {orderedAges.length === 0 ? (
                    <div className="text-[13px] text-[#64748B] py-6 italic">No schedule rows published for {country.label} yet.</div>
                ) : orderedAges.map((cohort) => (
                    <div key={`${cohort.age_months}::${cohort.age_label}`} className="bg-[#111827] border border-[#243044] rounded-xl overflow-hidden" data-testid={`country-cohort-${cohort.age_months}-${cohort.age_label.replace(/[^a-z0-9]/gi, '_').toLowerCase()}`}>
                        <div className="flex items-center justify-between px-5 py-3 bg-[#0B1220] border-b border-[#243044]">
                            <div className="flex items-center gap-2.5">
                                <Calendar className="w-4 h-4 text-[#56CCF2]" />
                                <h2 className="text-base font-semibold text-white">{cohort.age_label}</h2>
                                <span className="text-[11px] text-[#64748B]">· {cohort.rows.length} {cohort.rows.length === 1 ? "vaccine" : "vaccines"}</span>
                            </div>
                            <SaveButton
                                kind="vaccine_age_cohort"
                                refSlug={`${country.code.toLowerCase()}-${cohort.age_months}m`}
                                refTitle={`${country.label} ${cohort.age_label}`}
                                refUrl={`/vaccines/${country.code.toLowerCase()}#${cohort.age_months}m`}
                            />
                        </div>
                        <div className="divide-y divide-[#243044]">
                            {cohort.rows.map((r) => (
                                <Link
                                    key={r.id}
                                    to={`/vaccines/v/${r.vaccine_slug}`}
                                    className="flex items-center gap-4 px-5 py-3 hover:bg-[#0B1220] transition-colors group"
                                    data-testid={`country-row-${r.id}`}
                                >
                                    <Syringe className="w-3.5 h-3.5 text-[#56CCF2] shrink-0" />
                                    <div className="min-w-0 flex-1">
                                        <div className="text-sm text-white group-hover:text-[#56CCF2] transition-colors truncate">{r.vaccine_display_name}</div>
                                        <div className="text-[11px] text-[#64748B] mt-0.5 font-mono">
                                            Dose {r.dose_number} of {r.total_doses} · Schedule {r.schedule_year}
                                            {r.source_url ? <span className="ml-2"><ExternalLink className="w-3 h-3 inline" /></span> : null}
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                ))}
            </section>

            {/* Comparison CTA */}
            <section className="max-w-5xl mx-auto px-6 pb-16">
                <Link
                    to="/vaccines/compare/uk-vs-us"
                    data-testid="country-compare-link"
                    className="block bg-[#0B1220] border border-[#56CCF2]/30 hover:border-[#56CCF2] rounded-xl p-6 transition-colors"
                >
                    <div className="text-[10px] uppercase tracking-[0.28em] text-[#56CCF2] font-semibold mb-2">Continue exploring</div>
                    <h3 className="text-lg font-semibold text-white mb-1">Compare {country.label} vs {otherCountryCode.toUpperCase()} side-by-side</h3>
                    <p className="text-[13px] text-[#94A3B8]">See where the schedules diverge — and what the published evidence says about why.</p>
                </Link>
            </section>
        </div>
    );
}
